import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement} downloadLink
 * @property {HTMLSpanElement} downloadLabel
 * @property {HTMLParagraphElement} downloadStatus
 */

/** @extends Component<Refs> */
export class CatalogueDownload extends Component {
  requiredRefs = ['downloadLink', 'downloadLabel', 'downloadStatus'];

  /** @type {AbortController | undefined} */
  #request;

  /** @type {{ link: HTMLAnchorElement, label: HTMLSpanElement, text: string } | undefined} */
  #active;

  /** @type {Map<string, ReturnType<typeof setTimeout>>} */
  #objectUrls = new Map();

  #nativeRetry = false;

  connectedCallback() {
    super.connectedCallback();
    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /** @param {MouseEvent} event */
  async downloadPdf(event) {
    const { downloadLink: link, downloadLabel: label } = this.refs;
    if (!(link instanceof HTMLAnchorElement) || !(label instanceof HTMLSpanElement)) return;
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (this.#nativeRetry || typeof fetch !== 'function' || typeof URL.createObjectURL !== 'function') return;

    let destination;
    try {
      destination = new URL(link.href, window.location.href);
    } catch {
      return;
    }
    if (!['https:', 'http:'].includes(destination.protocol)) return;

    event.preventDefault();
    if (this.#request) return;

    const request = new AbortController();
    this.#request = request;
    this.#active = { link, label, text: label.textContent ?? '' };
    link.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');
    label.textContent = this.dataset.downloading ?? this.#active.text;
    this.#announce('');

    try {
      const response = await fetch(destination.href, {
        mode: 'cors',
        credentials: 'omit',
        signal: request.signal,
      });
      if (!this.#isCurrent(request, link)) return;
      if (!response.ok) throw new Error('PDF download request failed');

      const blob = await response.blob();
      if (!this.#isCurrent(request, link)) return;
      const header = await blob.slice(0, 5).text();
      if (!this.#isCurrent(request, link)) return;
      if (!blob.size || header !== '%PDF-') throw new Error('Download response is not a PDF');

      const objectUrl = URL.createObjectURL(blob);
      const revokeTimer = setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        this.#objectUrls.delete(objectUrl);
      }, 30000);
      this.#objectUrls.set(objectUrl, revokeTimer);

      const saveLink = document.createElement('a');
      saveLink.href = objectUrl;
      saveLink.download = link.download || this.dataset.filename || 'Amazing-Tiles-Hybrid-Flooring-2026.pdf';
      saveLink.hidden = true;
      document.body.append(saveLink);
      try {
        saveLink.click();
      } finally {
        saveLink.remove();
      }
      this.#announce(this.dataset.started ?? '');
    } catch {
      if (!this.#isCurrent(request, link)) return;
      this.#nativeRetry = true;
      this.#announce(this.dataset.failed ?? '', true);
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        this.#restoreLink();
      }
    }
  }

  /** @param {AbortController} request @param {HTMLAnchorElement} link */
  #isCurrent(request, link) {
    return this.#request === request && !request.signal.aborted && this.isConnected && this.refs.downloadLink === link;
  }

  /** @param {string} message @param {boolean} [isError] */
  #announce(message, isError = false) {
    const status = this.refs.downloadStatus;
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('visually-hidden', !isError);
    status.classList.toggle('is-error', isError);
  }

  #restoreLink() {
    if (!this.#active) return;
    this.#active.link.removeAttribute('aria-busy');
    this.#active.link.removeAttribute('aria-disabled');
    this.#active.label.textContent = this.#active.text;
    this.#active = undefined;
  }

  #reset() {
    this.#cleanup();
    this.#nativeRetry = false;
    this.#announce('');
  }

  #cleanup() {
    this.#request?.abort();
    this.#request = undefined;
    this.#restoreLink();
    for (const [objectUrl, timer] of this.#objectUrls) {
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
    }
    this.#objectUrls.clear();
  }
}

if (!customElements.get('catalogue-download')) {
  customElements.define('catalogue-download', CatalogueDownload);
}
